import { makeStyles, Grid, AdornmentBox, InputField, useClientOptions, DropdownSelect } from '@paintscout/ui'
import type { MuiTheme as Theme, DropdownSelectOption } from '@paintscout/ui'
import { QuoteContext } from '@paintscout/react-quote'
import type { QuoteDetails } from '@paintscout/util/builder'
import { getCompanyOptions, getDetails, getObjectLabels, getQuoteOptions, setDetails } from '@paintscout/util/builder'
import type { QuoteDocument } from 'paintscout'
import React, { useContext, useEffect, useState } from 'react'
import { parseNumber, roundPrice } from '@paintscout/util/calculator'
import DetailsItem from './DetailsItem'

const useStyles = makeStyles((theme: Theme) => ({
  margin: {
    marginTop: theme.spacing(0.25),
    marginBottom: theme.spacing(0.25)
  },
  alignRightInput: {
    textAlign: 'right'
  },
  customDiscount: {
    padding: theme.spacing(2)
  },
  select: {}
}))

interface DiscountItemProps {
  onChange: (args: { quote: QuoteDocument }) => void
  disabled: boolean
  expanded: boolean
  onExpand: (expanded: boolean) => void
}

export function DiscountItem(props: DiscountItemProps) {
  const { onChange, disabled, expanded, onExpand } = props
  const classes = useStyles()
  const { quote } = useContext(QuoteContext)
  const { options } = useClientOptions()
  const { currency } = getObjectLabels({ options })
  const companyOptions = getCompanyOptions({ options })
  const [details, setStateDetails] = useState(getDetails({ quote, options }))
  const { roundPrices } = getQuoteOptions({ quote })

  const isCustom = details.discount.key === 'custom'

  const discountOptions = (options.options.discounts?.order ?? []).map((discountKey) => ({
    ...options.options.discounts.values[discountKey],
    key: discountKey
  }))

  const dropdownOptions = [
    ...(quote.version <= 2 ? [{ key: 'no-discount', value: 0, label: 'No Discount', description: 'No Discount' }] : []),
    ...discountOptions.map((discount) => ({
      key: discount.key,
      value: parseNumber(discount.value, 0),
      label: `${discount.description} (${discount.type === 'amount' ? `$${parseNumber(discount.value, 0)}` : `${parseNumber(discount.value, 0)}%`})`,
      description: discount.description,
      type: discount.type
    })),
    {
      key: 'custom',
      value: 0,
      label: 'Custom...',
      description: 'Custom...',
      disabled:
        quote.version >= 2 &&
        details.discount?.keys?.length &&
        details.discount?.keys?.length === discountOptions.length
    }
  ]

  const discountDisplayAmount =
    details.discount.type === 'percentage' ? details.discount.rate : details.discount.total || details.discount.rate
  const sublabel =
    details.discount.type === 'percentage'
      ? `(${details.discount.rate}%)`
      : `(${currency.symbol}${discountDisplayAmount})`
  const label = `Discount${discountDisplayAmount ? ' ' + sublabel : ''}`

  const handleSelectDiscountLegacy = (item: DropdownSelectOption) => {
    if (!item) return
    const updatedDetails = {
      ...details,
      discount: {
        ...details.discount,
        key: item.key,
        rate: item.value,
        total: item.value,
        type: item.type,
        description: item.label
      }
    }

    setStateDetails(updatedDetails)
    onChange({ quote: setDetails({ quote, options, details: updatedDetails }) })
  }

  const handleSelectDiscount = (items: DropdownSelectOption[]) => {
    if (items.length === 0) {
      const updatedDetails = {
        ...details,
        discount: {
          ...details.discount,
          keys: [],
          key: '',
          rate: 0,
          total: 0,
          type: 'percentage',
          description: '',
          discounts: []
        }
      }
      setStateDetails(updatedDetails)
      onChange({ quote: setDetails({ quote, options, details: updatedDetails }) })
      return
    }

    if (items.length > 1 && items.length === dropdownOptions.length) {
      items = items.filter((item) => item.key !== 'custom')
    }

    const discountDetails = items.reduce(
      (acc, item) => {
        if (acc.key === 'custom') return acc

        if (item.key === 'custom') {
          return {
            ...acc,
            keys: [item.key],
            key: item.key,
            rate: item.value,
            total: item.value,
            type: item.type,
            description: item.description
          }
        }

        const currentItemValue =
          item.type === 'amount' ? item.value : (details.totals.afterAdjustment * item.value) / 100
        const roundedValue = roundPrice(parseNumber(currentItemValue, 0), roundPrices)

        return {
          ...acc,
          keys: acc.keys ? [...acc.keys, item.key] : [item.key],
          rate: acc.rate + (quote.version >= 10 ? roundedValue : currentItemValue),
          total: acc.total + (quote.version >= 10 ? roundedValue : currentItemValue),
          description: acc.description ? `${acc.description}, ${item.description}` : item.description
        }
      },
      {
        key: 'multi',
        keys: null,
        rate: 0,
        total: 0,
        type: 'amount',
        description: ''
      }
    )

    const updatedDetails = {
      ...details,
      discount: {
        ...details.discount,
        key: discountDetails.key,
        keys: discountDetails.keys,
        rate: discountDetails.rate,
        total: discountDetails.total,
        type: discountDetails.type,
        description: discountDetails.description,
        discounts:
          discountDetails.key === 'custom'
            ? []
            : items.map((item) => ({
                ...item,
                visible: true,
                price: item.value
              }))
      }
    }

    setStateDetails(updatedDetails)
    onChange({ quote: setDetails({ quote, options, details: updatedDetails }) })
  }

  const handleUpdateCustomDiscount = (key: string, value: string | number) => {
    const updatedDiscountDetails = {
      ...details.discount,
      key: 'custom',
      [key]: value,
      discounts: []
    }
    if (details.discount.type === 'percentage' && updatedDiscountDetails.type === 'amount' && quote.version >= 10) {
      updatedDiscountDetails.rate = roundPrice(parseNumber(details.discount.rate, 0), roundPrices)
    }

    const updatedDetails = {
      ...details,
      discount: updatedDiscountDetails
    }

    setStateDetails(updatedDetails)
    onChange({ quote: setDetails({ quote, options, details: updatedDetails }) })
  }

  useEffect(() => {
    setStateDetails(getDetails({ quote, options }))
  }, [quote])

  // Check so see if the quote was made before we added selectable discounts
  const legacyDiscount = !details.discount.key && details.discount.rate > 0

  const discountTypeOptions = [
    {
      label: '%',
      value: 'percentage'
    },
    {
      label: currency.symbol,
      value: 'amount'
    }
  ]

  // Check if missing discounts are present to show warning
  const missingDiscounts = details.discount.discounts
    ? details.discount.discounts.filter(
        (appliedDiscount) => !discountOptions.find((availableDiscount) => availableDiscount.key === appliedDiscount.key)
      )
    : []

  function warningMessage({ quote }: { quote: QuoteDocument }): {
    text: string
    link?: { text: string; id: string }
  } {
    const isTemplate = quote.type === 'quoteTemplate'
    return {
      text: 'This discount has been deleted in your settings.',
      link: !isTemplate && quote.fromTemplate?.key ? { text: 'Update Template', id: quote.fromTemplate.key } : undefined
    }
  }

  return (
    <DetailsItem
      label={label}
      value={details && details.discount && details.discount.total ? details.discount.total : null}
      expanded={expanded}
      onExpand={onExpand}
      warningMessage={missingDiscounts.length > 0 ? warningMessage({ quote }) : null}
      detailsComponent={
        <Grid className={classes.margin} container direction="row" justifyContent="flex-end" spacing={2}>
          {!legacyDiscount && (
            <Grid item xs={12}>
              {quote.version > 2 ? (
                <DropdownSelect
                  hideSelectAll={dropdownOptions.length === 1}
                  variant="multi"
                  label={'Select Rates'}
                  options={dropdownOptions}
                  value={dropdownOptions.filter(
                    (option) => option.key === details.discount.key || details.discount?.keys?.includes(option.key)
                  )}
                  disabled={disabled}
                  onChange={handleSelectDiscount}
                  pagination={{
                    currentPage: isCustom ? 1 : 0,
                    pages: [
                      <CustomDiscountForm
                        key="custom-discount"
                        details={details}
                        disabled={!companyOptions.discount.allowCustom || disabled}
                        handleChange={handleUpdateCustomDiscount}
                        discountTypeOptions={discountTypeOptions}
                        roundPrices={roundPrices}
                        quote={quote}
                      />
                    ],
                    onPageChange: (page) => {
                      if (page === 0) {
                        handleSelectDiscount([])
                      }
                    }
                  }}
                />
              ) : (
                <DropdownSelect
                  variant="single"
                  label={'Select Rate'}
                  options={dropdownOptions}
                  value={dropdownOptions.find((option) => option.key === details.discount.key) || dropdownOptions[0]}
                  disabled={disabled}
                  onChange={handleSelectDiscountLegacy}
                  pagination={{
                    currentPage: isCustom ? 1 : 0,
                    pages: [
                      <CustomDiscountForm
                        key="custom-discount"
                        details={details}
                        disabled={!companyOptions.discount.allowCustom || disabled}
                        handleChange={handleUpdateCustomDiscount}
                        discountTypeOptions={discountTypeOptions}
                        roundPrices={roundPrices}
                        quote={quote}
                      />
                    ],
                    onPageChange: (page) => {
                      if (page === 0) {
                        handleSelectDiscountLegacy(dropdownOptions[0])
                      }
                    }
                  }}
                />
              )}
            </Grid>
          )}

          {legacyDiscount && (
            <CustomDiscountForm
              details={details}
              disabled={!companyOptions.discount.allowCustom || disabled}
              handleChange={handleUpdateCustomDiscount}
              discountTypeOptions={discountTypeOptions}
              roundPrices={roundPrices}
              quote={quote}
            />
          )}
        </Grid>
      }
    />
  )
}

const CustomDiscountForm = ({
  details,
  disabled,
  handleChange,
  quote,
  discountTypeOptions,
  roundPrices
}: {
  details: QuoteDetails
  disabled: boolean
  handleChange: (key: string, value: string | number) => void
  discountTypeOptions: DropdownSelectOption[]
  quote: QuoteDocument
  roundPrices?: 'none' | 'up' | 'round'
}) => {
  const classes = useStyles()
  const handleValueChange = (key: string, value: string | number) => {
    if (details.discount.type === 'amount' && quote.version >= 10) {
      handleChange(key, roundPrice(parseNumber(value, 0), roundPrices))
    } else {
      handleChange(key, value)
    }
  }
  return (
    <Grid container className={classes.customDiscount} item justifyContent="flex-end">
      <Grid item xs={12}>
        <InputField
          name="rate"
          value={details.discount.rate !== undefined ? details.discount.rate : 0}
          disabled={disabled}
          fullWidth={true}
          margin="dense"
          format={'price'}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            handleValueChange('rate', parseFloat(event.target.value))
          }}
          classes={{ inputInput: classes.alignRightInput }}
          style={{ width: '100%' }}
          helperText={details.discount.isShown || details.discount.rate === 0 ? '' : `Included in Sub Total`}
          autoSelect={true}
          endAdornment={
            <AdornmentBox>
              <DropdownSelect
                name="type"
                variant="single"
                nestedDropdown
                value={
                  discountTypeOptions.find((option) => option.value === details.discount.type) || discountTypeOptions[0]
                }
                disabled={disabled}
                onChange={(option) => handleChange('type', option.value)}
                options={discountTypeOptions}
              />
            </AdornmentBox>
          }
        />
      </Grid>
      <Grid item xs={12}>
        <InputField
          name="description"
          value={details.discount.description}
          disabled={disabled}
          label={'Description'}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            handleChange('description', event.target.value)
          }}
          fullWidth={true}
          margin="dense"
        />
      </Grid>
    </Grid>
  )
}
